/**
 * Secure Cookie Utility
 *
 * This utility provides secure cookie management with proper security flags
 * to protect against XSS and CSRF attacks.
 *
 * NOTE:
 * - The actual authentication token is stored only in the shared v2-frontend
 *   cookie format (`d_c_c_t_p_*`).
 * - The older `ntra_assessment_compliance_token` cookie is no longer used.
 */

import { getV2Token, clearV2Token } from './v2AuthUtils';
import { setV2Token } from './setV2Token';

export interface CookieOptions {
  expires?: Date;
  maxAge?: number;
  path?: string;
  domain?: string;
  secure?: boolean;
  httpOnly?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

export class SecureCookieManager {
  private static readonly REFRESH_TOKEN_COOKIE_NAME = 'ntra_assessment_compliance_refresh_token';
  
  // Default secure cookie options
  private static readonly DEFAULT_OPTIONS: CookieOptions = {
    path: '/',
    secure: process.env.NODE_ENV === 'production' || window.location.protocol === 'https:',
    httpOnly: false, // We need to set this from JavaScript, but backend should set HttpOnly
    sameSite: 'Strict'
  };

  /**
   * Set a secure cookie with proper security flags
   */
  static setCookie(name: string, value: string, options: CookieOptions = {}): void {
    const cookieOptions = { ...this.DEFAULT_OPTIONS, ...options };
    
    let cookieString = `${name}=${encodeURIComponent(value)}`;
    
    if (cookieOptions.expires) {
      cookieString += `; expires=${cookieOptions.expires.toUTCString()}`;
    }
    
    if (cookieOptions.maxAge) {
      cookieString += `; max-age=${cookieOptions.maxAge}`;
    }
    
    if (cookieOptions.path) {
      cookieString += `; path=${cookieOptions.path}`;
    }
    
    if (cookieOptions.domain) {
      cookieString += `; domain=${cookieOptions.domain}`;
    }
    
    if (cookieOptions.secure) {
      cookieString += `; secure`;
    }
    
    if (cookieOptions.httpOnly) {
      cookieString += `; httponly`;
    }
    
    if (cookieOptions.sameSite) {
      cookieString += `; samesite=${cookieOptions.sameSite}`;
    }
    
    document.cookie = cookieString;
  }

  /**
   * Get a cookie value by name
   */
  static getCookie(name: string): string | null {
    const nameEQ = name + "=";
    const cookies = document.cookie.split(';');
    
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1, cookie.length);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
      }
    }
    return null;
  }

  /**
   * Delete a cookie by setting it to expire in the past
   */
  static deleteCookie(name: string, path: string = '/'): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
  }

  /**
   * Set authentication token.
   *
   * For this app we delegate to the shared v2 token cookies (d_c_c_t_p_*),
   * so no separate `ntra_assessment_compliance_token` cookie is created.
   */
  static setAuthToken(token: string, rememberMe: boolean = false): void {
    setV2Token(token, rememberMe);
  }

  /**
   * Get authentication token from cookies (v2 format).
   */
  static getAuthToken(): string | null {
    return getV2Token();
  }

  /**
   * Set refresh token securely
   */
  static setRefreshToken(token: string, rememberMe: boolean = false): void {
    const options: CookieOptions = {
      ...this.DEFAULT_OPTIONS,
      maxAge: rememberMe ? 30 * 24 * 60 * 60 : 7 * 24 * 60 * 60, // 30 days or 7 days
    };
    
    this.setCookie(this.REFRESH_TOKEN_COOKIE_NAME, token, options);
  }

  /**
   * Get refresh token from cookie
   */
  static getRefreshToken(): string | null {
    return this.getCookie(this.REFRESH_TOKEN_COOKIE_NAME);
  }

  /**
   * Clear all authentication cookies.
   *
   * This clears the shared v2 token cookies and the local refresh token cookie.
   */
  static clearAuthCookies(): void {
    clearV2Token();
    this.deleteCookie(this.REFRESH_TOKEN_COOKIE_NAME);
  }

  /**
   * Check if user is authenticated by checking for the shared v2 token.
   */
  static isAuthenticated(): boolean {
    return !!getV2Token();
  }

  /**
   * Get all cookies (for debugging purposes)
   */
  static getAllCookies(): Record<string, string> {
    const cookies: Record<string, string> = {};
    document.cookie.split(';').forEach(cookie => {
      const [name, value] = cookie.trim().split('=');
      if (name && value) {
        cookies[name] = decodeURIComponent(value);
      }
    });
    return cookies;
  }
}

export default SecureCookieManager;
